import type { IPickPageCurrentCampaignUsage, IPickPageOtherCampaignTargets } from '../types';
import type { Campaign } from '~/modules/campaign/types';

import { useCampaignVariantTargetsQuery } from '~/api/graphql/queries/campaignVariantTargets.generated';
import { toCurrentCampaignUsageByPageKey, toOtherCampaignTargets } from '../utils';
import { PICK_PAGE_USAGE_QUERY_VARIABLES } from '../constants';
import { captureSentryError } from '~/exceptions/sentry';
import { useCallback, useMemo } from 'react';

export interface IUsePickPageUsageParams {
  campaign?: Campaign;
  targetVariantId?: string;
}

export interface IUsePickPageUsageResult {
  otherCampaignTargets: IPickPageOtherCampaignTargets;
  currentCampaignUsageByPageKey: Record<string, IPickPageCurrentCampaignUsage>;
  isLoadingUsage: boolean;
  handleRefetchUsage: () => Promise<void>;
}

/** Resolves which pages are already locked by another experiment, whatever kind of test holds them. */
export const usePickPageUsage = ({
  campaign,
  targetVariantId,
}: IUsePickPageUsageParams): IUsePickPageUsageResult => {
  // Another experiment can claim or release a page while this tab sits on its cache, and the 7-day
  // global staleTime would otherwise serve that stale answer on every return to the setup screen.
  const { data, isFetching, refetch } = useCampaignVariantTargetsQuery(
    PICK_PAGE_USAGE_QUERY_VARIABLES,
    {
      refetchOnMount: 'always',
      onError: (error) =>
        captureSentryError(error, {
          source: 'usePickPageUsage',
          level: 'warning',
          tags: { experiment: 'Experiment' },
        }),
    },
  );

  const otherCampaignTargets = useMemo(
    () =>
      toOtherCampaignTargets({
        campaignVariantTargets: data?.campaignVariantTargets ?? [],
        excludedCampaignId: campaign?.id,
      }),
    [data, campaign?.id],
  );

  const currentCampaignUsageByPageKey = useMemo(
    () => toCurrentCampaignUsageByPageKey({ campaign, targetVariantId }),
    [campaign, targetVariantId],
  );

  const handleRefetchUsage = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    otherCampaignTargets,
    currentCampaignUsageByPageKey,
    isLoadingUsage: isFetching,
    handleRefetchUsage,
  };
};
